import { apiClient } from './client';
import { BoardItem, FolderTreeNode } from '../types/boardContent';

export async function fetchFolderTree(boardId: number): Promise<FolderTreeNode[]> {
  const { data } = await apiClient.get<FolderTreeNode[]>(`/boards/${boardId}/tree`);
  return data;
}

export async function fetchContents(
  boardId: number,
  folderId?: number | null,
  contractId?: number | null
): Promise<BoardItem[]> {
  const params: Record<string, number> = {};
  if (folderId != null) params.folderId = folderId;
  if (contractId != null) params.contractId = contractId;
  const { data } = await apiClient.get<BoardItem[]>(`/boards/${boardId}/contents`, {
    params,
  });
  return data;
}

export async function searchFolders(
  boardId: number,
  keyword: string
): Promise<FolderTreeNode[]> {
  const { data } = await apiClient.get<FolderTreeNode[]>(`/boards/${boardId}/folders/search`, {
    params: { q: keyword },
  });
  return data;
}

export async function createFolder(
  boardId: number,
  folderName: string,
  parentFolderId?: number | null
): Promise<BoardItem> {
  const { data } = await apiClient.post<BoardItem>(`/boards/${boardId}/folders`, {
    folderName,
    parentFolderId: parentFolderId ?? null,
  });
  return data;
}

export async function renameFolder(
  boardId: number,
  folderId: number,
  name: string
): Promise<BoardItem> {
  const { data } = await apiClient.patch<BoardItem>(`/boards/${boardId}/folders/${folderId}`, {
    name,
  });
  return data;
}

export async function moveFolder(
  boardId: number,
  folderId: number,
  direction: 'UP' | 'DOWN'
): Promise<BoardItem> {
  const { data } = await apiClient.patch<BoardItem>(
    `/boards/${boardId}/folders/${folderId}/move`,
    { direction }
  );
  return data;
}

export async function reorderFolders(boardId: number, folderIds: number[]): Promise<void> {
  await apiClient.put(`/boards/${boardId}/folders/reorder`, { folderIds });
}

export async function deleteFolder(boardId: number, folderId: number): Promise<void> {
  await apiClient.delete(`/boards/${boardId}/folders/${folderId}`);
}

export async function uploadFile(
  boardId: number,
  file: File,
  folderId?: number | null,
  contractId?: number | null
): Promise<BoardItem> {
  const formData = new FormData();
  formData.append('file', file);
  const params: Record<string, number> = {};
  if (folderId != null) params.folderId = folderId;
  if (contractId != null) params.contractId = contractId;
  const { data } = await apiClient.post<BoardItem>(`/boards/${boardId}/files`, formData, {
    params,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function renameFile(
  boardId: number,
  fileId: number,
  name: string
): Promise<BoardItem> {
  const { data } = await apiClient.patch<BoardItem>(`/boards/${boardId}/files/${fileId}`, {
    name,
  });
  return data;
}

export async function deleteFile(boardId: number, fileId: number): Promise<void> {
  await apiClient.delete(`/boards/${boardId}/files/${fileId}`);
}

export async function downloadFile(boardId: number, fileId: number, fileName: string) {
  const { data } = await apiClient.get<Blob>(`/boards/${boardId}/files/${fileId}/download`, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(url);
}

/** 미리보기용 blob 로드 (다운로드와 동일 API) */
export async function fetchFileBlob(boardId: number, fileId: number): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`/boards/${boardId}/files/${fileId}/download`, {
    responseType: 'blob',
  });
  return data;
}

export async function mergeBoardPdf(boardId: number, folderIds: number[]): Promise<BoardItem> {
  const { data } = await apiClient.post<BoardItem>(
    `/boards/${boardId}/merge-pdf`,
    { folderIds },
    { timeout: 10 * 60 * 1000 }
  );
  return data;
}
